import type { ApiNetwork, ApiWalletPrepaidOverview } from '../api/types';
import type { Account } from '../global/types';

import { parseAccountId } from './account';

export type PrepaidHistoryWallet = { address: string; title: string };
export type PrepaidHistoryFilter = 'current' | 'all';
export type PrepaidHistoryItem = {
  id: string;
  type: string;
  ownerAddress: string;
  amount: string;
  asset?: string;
  txHash?: string;
  description?: string;
  createdAt: string;
  status: string;
};

export function getPrepaidHistoryWallets(accounts: Record<string, Account> | undefined, network: ApiNetwork) {
  const wallets = new Map<string, PrepaidHistoryWallet>();
  Object.entries(accounts ?? {}).forEach(([accountId, account]) => {
    const address = account.byChain.tron?.address;
    if (!address || account.isTemporary || parseAccountId(accountId).network !== network) return;
    if (!wallets.has(address)) wallets.set(address, { address, title: account.title || '' });
  });
  return [...wallets.values()];
}

export function filterPrepaidHistory(
  overview: ApiWalletPrepaidOverview | undefined,
  wallets: PrepaidHistoryWallet[],
  currentAddress: string | undefined,
  filter: PrepaidHistoryFilter,
): PrepaidHistoryItem[] {
  if (!overview) return [];
  const localAddresses = new Set(wallets.map(({ address }) => address));
  const includes = (address: string | null | undefined): address is string => Boolean(
    address && localAddresses.has(address)
    && (filter === 'all' || (filter === 'current' && address === currentAddress)),
  );

  // An unassigned group entry is not evidence that it belongs to a local wallet.
  return [
    ...overview.entries.filter((entry) => includes(entry.owner_address)).map((entry) => ({
      id: entry.id,
      type: entry.type,
      ownerAddress: entry.owner_address!,
      amount: entry.amount_trx,
      asset: entry.asset_amount ? `${entry.asset_amount} ${entry.asset_code}` : undefined,
      txHash: entry.tx_hash,
      description: entry.description,
      createdAt: entry.created_at,
      status: 'completed',
    })),
    ...overview.topups.filter((topup) => includes(topup.owner_address)).map((topup) => ({
      id: topup.id,
      type: 'topup',
      ownerAddress: topup.owner_address!,
      amount: String(Number(topup.credited_trx) - Number(topup.resource_charge_trx)),
      asset: `${topup.amount} ${topup.asset_code}`,
      txHash: topup.tx_hash,
      description: 'Prepaid top-up',
      createdAt: topup.created_at,
      status: topup.status,
    })),
  ].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
}
